import { IsEqual } from 'type-fest';
import * as z from 'zod';

import { AppraisalId, InspectionId, PropertyId, ReportableEventId } from '../BrandedIds';
import { Expect } from '../TypeAssertions';
import { zodBrandedUuid, zodDateOrString } from '../utils';
import { InspectionType } from './Inspection';

const ReportableEventDate = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('plainDate'),
    date: zodDateOrString,
  }),
  z.object({
    type: z.literal('timestamp'),
    timestamp: zodDateOrString,
  }),
]);

export const ReportableEventManagedFieldsShape = {
  id: zodBrandedUuid<ReportableEventId>(), // ID -- generated by willow
  createdAt: zodDateOrString, // willow created date
  updatedAt: zodDateOrString, // willow updated date
};
export type ReportableEventManagedFieldsShape = typeof ReportableEventManagedFieldsShape;

type ReportableEventInputOf<T extends z.ZodObject<{}>> = T extends z.ZodObject<infer S>
  ? z.ZodObject<{
      // TODO #9105 remove eventTimestamp once migration is done
      [K in keyof S as K extends 'createdAt' | 'updatedAt' | 'eventTimestamp' ? never : K]: K extends 'id'
        ? z.ZodOptional<S[K]>
        : S[K] extends z.core.$ZodType<any, any, z.core.$ZodTypeInternals<any, any>>
        ? S[K]
        : never;
    }>
  : never;

function inputOf<T extends z.ZodObject<{}>>(event: T): ReportableEventInputOf<T> {
  // TODO #9105 remove eventTimestamp once migration is done
  return event.omit({ id: true, createdAt: true, updatedAt: true, eventTimestamp: true }).extend({
    /**
     * Event ID
     *
     * - If not provided:
     *   - handler will generate an ID while inserting a new event.
     * - If provided:
     *   - If an event with the provided ID already exists:
     *     - handler will update the event with the provided ID.
     *   - If an event with the provided ID does not exist:
     *     - handler will insert a new event with the provided ID.
     */
    id: zodBrandedUuid<ReportableEventId>().optional(),
  }) as unknown as ReportableEventInputOf<T>;
}

export const BaseReportableEvent = z.object({
  ...ReportableEventManagedFieldsShape,
  /**
   *  Date the event actually occurred
   *  TODO: #9105 remove once migration is done
   */
  date: ReportableEventDate.optional(),

  /**
   * @deprecated Use {@link ReportableEvent.date} instead
   */
  eventTimestamp: zodDateOrString.optional(),
});

/** APPRAISALS */
const AppraisalType = z.enum(['bpo', 'appraisal']);
type AppraisalType = z.infer<typeof AppraisalType>;

export type AppraisalOrderedReportableEvent = z.infer<typeof AppraisalOrderedReportableEvent>;
export const AppraisalOrderedReportableEvent = BaseReportableEvent.extend({
  type: z.literal('appraisalOrdered'),
  propertyId: zodBrandedUuid<PropertyId>(),
  vendorName: z.string(),
  /**
   * @TODO This field is not populated yet
   */
  appraisalType: AppraisalType.optional(),
});
export const AppraisalOrderedReportableEventInput = inputOf(AppraisalOrderedReportableEvent);

export type AppraisalObtainedReportableEvent = z.infer<typeof AppraisalObtainedReportableEvent>;
export const AppraisalObtainedReportableEvent = BaseReportableEvent.extend({
  type: z.literal('appraisalObtained'),
  appraisalId: zodBrandedUuid<AppraisalId>(),
});
export const AppraisalObtainedReportableEventInput = inputOf(AppraisalObtainedReportableEvent);

/** INSPECTIONS */
export const InspectionOrderedReason = z.enum(['collections', 'foreclosure', 'lossMitigation', 'bankruptcy', 'other']);
export type InspectionOrderedReason = z.infer<typeof InspectionOrderedReason>;

export const InspectionOrderedReportableEvent = BaseReportableEvent.extend({
  type: z.literal('inspectionOrdered'),
  propertyId: zodBrandedUuid<PropertyId>(),
  inspectionType: InspectionType,
  inspectionReason: InspectionOrderedReason,
  vendorName: z.string(),
});
export type InspectionOrderedReportableEvent = z.infer<typeof InspectionOrderedReportableEvent>;
export const InspectionOrderedReportableEventInput = inputOf(InspectionOrderedReportableEvent);

export const InspectionObtainedReportableEvent = BaseReportableEvent.extend({
  type: z.literal('inspectionObtained'),
  inspectionId: zodBrandedUuid<InspectionId>(),
});
export type InspectionObtainedReportableEvent = z.infer<typeof InspectionObtainedReportableEvent>;
export const InspectionObtainedReportableEventInput = inputOf(InspectionObtainedReportableEvent);

/**
 * ALL REPORTABLE EVENTS
 */
export const ReportableEvent = z.discriminatedUnion('type', [
  AppraisalOrderedReportableEvent,
  AppraisalObtainedReportableEvent,
  InspectionOrderedReportableEvent,
  InspectionObtainedReportableEvent,
]);
export type ReportableEvent = z.infer<typeof ReportableEvent>;

export const ReportableEventInput = z.discriminatedUnion('type', [
  AppraisalOrderedReportableEventInput,
  AppraisalObtainedReportableEventInput,
  InspectionOrderedReportableEventInput,
  InspectionObtainedReportableEventInput,
]);
export type ReportableEventInput = z.infer<typeof ReportableEventInput>;

/**
 * Remember to keep ReportableEvent and ReportableEventInput in sync.
 * If you see a type error here it's most likely because they're not.
 */
type _testInputTypesEqualEventTypes = Expect<IsEqual<ReportableEventInput['type'], ReportableEvent['type']>>;
